import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDeleteDefaultAdminButton — Round P112k (Jul 31, 2026).
 *
 * One-shot cleanup button that removes the auto-seeded
 * `admin@example.com` account from the users collection. The seed
 * was originally there as a developer starter credential, but on
 * production it became a security hole (bots probing the forgot-
 * password endpoint got real reset emails routed to a bounced
 * `example.com` address, revealing the account exists).
 *
 * Button self-hides when there's nothing to clean up (status endpoint
 * reports `exists=false`). Refuses to run when the caller IS the
 * default admin (would lock them out).
 */
export default function AdminDeleteDefaultAdminButton() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/danger/default-admin-status`,
        { withCredentials: true },
      );
      setStatus(data);
    } catch {
      // silent — if this fails, we just hide the button
      setStatus({ exists: false });
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') loadStatus();
  }, [user, loadStatus]);

  if (user?.role !== 'admin') return null;
  if (!status?.exists) return null;

  const runDelete = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/danger/delete-default-admin`,
        {},
        { withCredentials: true },
      );
      toast.success('Default admin removed', {
        description: `Deleted ${data.users_deleted} user record, ${data.reset_tokens_deleted} reset tokens, ${data.sessions_deleted} sessions. Bots probing admin@example.com will now see "user not found".`,
        duration: 8000,
      });
      setConfirmOpen(false);
      await loadStatus();
    } catch (err) {
      toast.error('Cleanup failed', {
        description: err?.response?.data?.detail || err?.message,
        duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-rose-500/15 hover:bg-rose-500/25 text-rose-900 dark:text-rose-200 border border-rose-500/40 transition-colors"
        title="One-click removal of the seeded admin@example.com account. Fixes the bounce-email spam bots trigger by probing forgot-password."
        data-testid="admin-delete-default-admin-fire"
      >
        <ShieldOff className="w-4 h-4" />
        Remove admin@example.com
      </button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="admin-delete-default-admin-confirm"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Remove the default admin account?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm pt-2">
                <p>
                  This deletes the seeded <span className="font-mono font-bold">admin@example.com</span> user
                  from your production database, along with any password-reset tokens and sessions tied to it.
                </p>
                <p className="text-rose-700 dark:text-rose-400 font-semibold">
                  You&apos;ll still be logged in as admin via your Google account — this only removes the
                  ghost account that bots have been probing.
                </p>
                <p className="text-xs text-muted-foreground">
                  After this, forgot-password requests for that email will return &quot;user not found&quot; and
                  no more bounce-email bursts will arrive in your Gmail. Safe to run — it&apos;s
                  idempotent, so you can re-run it later without harm.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm font-bold border border-border hover:bg-muted disabled:opacity-60"
              data-testid="admin-delete-default-admin-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={runDelete}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60"
              data-testid="admin-delete-default-admin-confirm-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
              Remove it permanently
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
